import styled from "styled-components";
import Draggable, { DraggableData, DraggableEvent } from "react-draggable";
import DevicesIcon from "@mui/icons-material/Devices";
import { useEffect, useState } from "react";
import { Socket } from "socket.io-client";
import { Navigate, useParams, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import { LanguageMap } from "Language";

import { buildSocket } from "components/socket";
import ErrorPage from "components/common/ErrorPage";
import Layout from "components/common/Layout";
import emitAsyncAnswer from "components/helper/emitAsyncAnswer";
import { useUser } from "components/helper/userContext";
import { useSession } from "components/helper/sessionContext";
import { useToken } from "components/helper/tokenContext";
import {
    ADDITIONAL_FIELDS_API,
    getCurrentUser,
} from "components/helper/callApi";
import ChoiceQuestion from "components/vote/Quizzbox/Question/ChoiceQuestion/ChoiceQuestion";
import GroupedQuestion from "components/vote/Quizzbox/Question/GroupedQuestion/GroupedQuestion";
import OrderedQuestion from "components/vote/Quizzbox/Question/OrderedQuestion/OrderedQuestion";
import Deliberation from "components/vote/Votebox/Deliberation/Deliberation";
import GroupedDeliberation from "components/vote/Votebox/Deliberation/GroupedDeliberation/GroupedDeliberation";
import Election from "components/vote/Votebox/Election/Election";
import GroupedEvaluation from "components/vote/Quizzbox/DisplayMode/Question/GroupedEvaluation/GroupedEvaluation";
import NumericQuestion from "components/vote/Quizzbox/common/NumericQuestion";
import VoteHeader from "components/vote/Votebox/common/Header/VoteHeader";
import TagCloudQuestion from "components/vote/Quizzbox/Question/TagCloudQuestion";
import NoVoteAllowedMessage from "components/vote/Votebox/common/NoVoteAllowedMessage";
import DisableStreamingLogo from "components/zoomIcons/deny_visio.svg";
import EnableStreamingLogo from "components/zoomIcons/allow_visio.svg";

import {
    loadZoomMeetingClient,
    toggleZoomDisplay,
} from "services/zoomMeetingService";
import { getAdditionalFields } from "services/additionalFields";
import {
    UnknownError,
    NetworkError,
    DisplayedError,
    HTTPResponseError,
} from "services/errors";
import {
    Customization,
    saveCustomization,
} from "services/customizationService";
import { getSubdomain } from "services/urlService";

import {
    LS_CUSTOMIZATION_SET_EVENT_KEY,
    LS_WAITING_TEXT_KEY,
} from "constants/constants";
import { QuestionType } from "types/questionTypes";
import { DataVoteType } from "types/dataVoteType";
import { User } from "types/User";
import { EVENT } from "types/socketEventsType";
import { SessionType } from "types/sessionType";

type DragInfo = {
    x: number;
    y: number;
    time: number;
};

type WaitingTextType = Record<string, string>;

const ALLOWED_ZOOM_VIDEO_LS_KEY = "allowedZoomVideo";

const SessionPage = () => {
    const { t, i18n } = useTranslation();
    const { user, setUser } = useUser();
    const { onlineCode } = useParams();
    const { token } = useToken();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const {
        session,
        setSession,
        socket,
        setSocket,
        setSessionIsActive,
        setShowRequestFloorButton,
        setIsRequestingFloor,
    } = useSession();

    const subdomain = getSubdomain();

    const [vote, setVote] = useState<DataVoteType | null>(null);
    const [error, setError] = useState<DisplayedError | null>(null);
    const [isSocketError, setIsSocketError] = useState(false);
    const [liveDeviceCount, setDeviceCountChanged] = useState<number>(0);
    const [dragInfo, setDragInfo] = useState<DragInfo | null>(null);
    const [isVideoAllowed, setIsAllowedVideoAllowed] = useState<boolean>(() => {
        const allowedZoomVideoCache = localStorage.getItem(
            ALLOWED_ZOOM_VIDEO_LS_KEY,
        );
        return allowedZoomVideoCache
            ? (JSON.parse(allowedZoomVideoCache) as boolean)
            : true;
    });

    const updateWaitingText = (): WaitingTextType | null => {
        const waitingTextCache = localStorage.getItem(LS_WAITING_TEXT_KEY);

        if (!waitingTextCache || waitingTextCache === "undefined") {
            return null;
        }

        return JSON.parse(waitingTextCache);
    };

    const [waitingText, setWaitingText] = useState<WaitingTextType | null>(() =>
        updateWaitingText(),
    );

    useEffect(() => {
        localStorage.setItem(
            ALLOWED_ZOOM_VIDEO_LS_KEY,
            isVideoAllowed.toString(),
        );
        toggleZoomDisplay(isVideoAllowed ? "block" : "none");
        return () => {
            toggleZoomDisplay("none");
        };
    }, [isVideoAllowed, onlineCode]);

    useEffect(() => {
        if (user && user.onlineCode !== onlineCode) {
            setUser(null);
            return;
        }

        let tmpSocket: Socket | null = socket;
        if (!tmpSocket && token) {
            tmpSocket = buildSocket(onlineCode as string);
            setSocket(tmpSocket);
        }

        if (tmpSocket && !tmpSocket.connected) {
            connectWebsocketToServer(tmpSocket);
        }

        return () => {
            if (tmpSocket) {
                clearAllSocketListeners(tmpSocket);
                if (tmpSocket.connected) tmpSocket.disconnect();
                console.log("Socket disconnected");
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onlineCode]);

    useEffect(() => {
        if (!subdomain) return;

        const handleCustomizationSet = () => {
            setWaitingText(updateWaitingText());
        };

        window.addEventListener(
            LS_CUSTOMIZATION_SET_EVENT_KEY,
            handleCustomizationSet,
        );

        return () => {
            window.removeEventListener(
                LS_CUSTOMIZATION_SET_EVENT_KEY,
                handleCustomizationSet,
            );
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const connectWebsocketToServer = (socket: Socket): void => {
        let reconnectTimeout: NodeJS.Timeout | null = null;
        let isReconnecting = false;

        const clearReconnectTimeout = () => {
            if (reconnectTimeout) {
                clearTimeout(reconnectTimeout);
                reconnectTimeout = null;
            }
        };

        const attemptReconnect = () => {
            if (isReconnecting || socket.connected) return;

            isReconnecting = true;
            console.warn(
                "Socket déconnecté, tentative de reconnexion dans 5s...",
            );

            reconnectTimeout = setTimeout(() => {
                if (!socket.connected) {
                    console.log("Tentative de reconnexion...");
                    socket.connect();
                    setIsSocketError(true);
                }
                isReconnecting = false;
            }, 5000);
        };

        const registerHandlers = () => {
            Object.values(EVENT).forEach((event) =>
                socket.off(event as string),
            );

            socket.on(EVENT.CONNECT, () => {
                console.log("Socket connected");
                setIsSocketError(false);
                isReconnecting = false;
                clearReconnectTimeout();
            });

            socket.on(
                EVENT.DEVICE_COUNT_CHANGED,
                ({ liveDeviceCount }: { liveDeviceCount: number }) => {
                    setDeviceCountChanged(liveDeviceCount);
                },
            );

            socket.on(EVENT.VOTE_REQUEST, voteRequestHandler);

            socket.on(EVENT.CLOSE_VOTE, () => setVote(null));

            socket.on(EVENT.RELOAD_REQUEST, reloadRequest);

            socket.on(EVENT.KILL_PARTICIPANTS, redirectToHome);

            socket.on(EVENT.SESSION_CHANGE_STATUS, (isActive: boolean) => {
                setSessionIsActive(isActive);
                if (!isActive) {
                    setVote(null);
                    setShowRequestFloorButton(false);
                    setIsRequestingFloor(false);
                }
            });

            socket.on(
                EVENT.ENTITY_CUSTOMIZATION_UPDATE,
                (params: Customization) => {
                    saveCustomization(params);
                },
            );

            socket.on(EVENT.DISCONNECT, () => {
                console.warn("Socket disconnected");
                setSessionIsActive(false);
                setVote(null);
                attemptReconnect();
            });

            socket.on(EVENT.CONNECT_ERROR, (err) => {
                console.warn("Socket ERROR:", err.message);
                if (err.message === "jwt expired") {
                    redirectToHome();
                    return;
                }
                attemptReconnect();
            });

            socket.on(EVENT.REQUEST_FOR_FLOOR, (show: boolean) => {
                setShowRequestFloorButton(!!show);
                if (!show) setIsRequestingFloor(false);
            });

            socket.on(
                EVENT.TOGGLE_PARTICIPANT_FLOOR_REQUEST,
                (value: boolean) => {
                    setIsRequestingFloor(value);
                },
            );

            socket.on(EVENT.UPDATE_PARTICIPANT, (newUser: User) => {
                setUser((oldUser) =>
                    oldUser ? { ...oldUser, ...newUser } : newUser,
                );
            });
        };

        registerHandlers();
        socket.connect();
    };
    const voteRequestHandler = async (vote: any) => {
        try {
            const userData: User = await getCurrentUser(
                getAdditionalFields({
                    shouldCountOnlineDevice: (session as SessionType).scenario
                        .canHaveMultiConnexion,
                    fields: [ADDITIONAL_FIELDS_API.PROXIES],
                }),
            );

            setUser(userData);
            setVote(vote.vote);
            setDeviceCountChanged((userData as User).liveDeviceCount || 0);
        } catch (error) {
            if (error instanceof HTTPResponseError) {
                switch (error.response.status) {
                    case 401:
                        enqueueSnackbar(`${t("errorYouMustBeLog")}`, {
                            variant: "error",
                            persist: true,
                        });
                        break;
                    default:
                        setError(new UnknownError());
                }
            } else {
                setError(new NetworkError());
            }
        }
    };

    const reloadRequest = () => {
        navigate(`/session/${onlineCode}`);
    };

    const redirectToHome = () => {
        setUser(null);
        navigate(`/`, { replace: true });
    };

    const clearAllSocketListeners = (socketParams: Socket) => {
        Object.values(EVENT).forEach((event) => {
            socketParams.off(event);
        });
    };

    const sendQuizAnswer = (answer: string | Array<string>) => {
        const question = vote?.questions[0];

        return emitAsyncAnswer(socket, {
            votingDeviceId: user?.id as string,
            publicKey: vote?.publicKey as string,
            answers: Array.isArray(answer)
                ? answer.map((ans) => ({
                      questionId: question?.id as number,
                      answer: ans,
                      weight: user?.weight as number,
                  }))
                : [
                      {
                          questionId: question?.id as number,
                          answer: answer,
                          weight: user?.weight as number,
                      },
                  ],
        });
    };

    const renderNoVotePendingTemplate = () => {
        return (
            <NoVoteStyled>
                {waitingText && waitingText[i18n.language] ? (
                    <p>
                        {waitingText[i18n.language]
                            .split("\r\n")
                            .map((line: string) => (
                                <>
                                    {line}
                                    <br />
                                </>
                            ))}
                    </p>
                ) : (
                    <>
                        <ConnectedMessage>
                            {t("youAreConnected")}
                        </ConnectedMessage>
                        <p>
                            {t(
                                `noQuestionPending.${window.env.REACT_APP_THEME}`,
                            )}
                        </p>
                        <p>{t("pageRefreshAutomatically")}</p>
                    </>
                )}
            </NoVoteStyled>
        );
    };

    const renderComponentVoteTemplate = () => {
        //This check at this point does not make sens
        //It is only used as non-null typing
        //Makes User not null for linter
        if (!user) {
            return;
        }

        if (!vote) {
            return renderNoVotePendingTemplate();
        }

        const question = vote.questions[0];

        if (user.hasGivenProxy) {
            return (
                <>
                    <VoteHeader
                        question={question}
                        voteTitle={vote.voteTitle}
                    />
                    <StyledNoVoteParagraph>{`${t("noVoteCauseProxy")}`}</StyledNoVoteParagraph>
                </>
            );
        }

        const noActiveProxies = user.proxies.every(
            (proxy) => proxy.weight <= 0,
        );

        if (0 === user.weight && noActiveProxies) {
            return (
                <>
                    <VoteHeader
                        question={question}
                        voteTitle={vote.voteTitle}
                    />
                    <NoVoteAllowedMessage displayName={user.displayName} />
                </>
            );
        }

        if (vote.questions.length > 1) {
            return (
                <GroupedDeliberation
                    title={vote.voteTitle}
                    questions={vote.questions}
                    user={user}
                    proxies={user.proxies}
                    publicKey={vote.publicKey}
                />
            );
        }

        //Always return InputComponent for tag cloud
        if (question?.type === QuestionType.TAG_CLOUD) {
            return (
                <TagCloudQuestion
                    title={question.title}
                    onValidate={sendQuizAnswer}
                    numberOfInputs={question.maxNumberOfAnswers}
                    userCanSubmitMultipleTimes={question.multiSubmit}
                />
            );
        }

        switch (question?.type) {
            case QuestionType.NUMERICAL_CORRECT_ANSWER:
            case QuestionType.NOTE:
            case QuestionType.NUMERICAL:
            case QuestionType.ROLL_CALL:
            case QuestionType.TEST:
            case QuestionType.ID_ROLL_CALL:
            case QuestionType.ANONYMOUS_ROLL_CALL:
                return (
                    <NumericQuestion
                        question={question}
                        id={user.id}
                        useDecimal={question.useDecimal}
                        minValue={question.minValue}
                        maxValue={question.maxValue}
                        publicKey={vote.publicKey}
                    />
                );
            case QuestionType.MULTIPLE_CHOICE_CORRECT_ANSWER:
            case QuestionType.MULTIPLE_CHOICE:
            case QuestionType.UNIQUE_CHOICE:
            case QuestionType.UNIQUE_CHOICE_CORRECT_ANSWER:
            case QuestionType.LIST:
            case QuestionType.TEAMS:
            case QuestionType.CLASSIFICATION:
                return (
                    <ChoiceQuestion
                        onValidate={sendQuizAnswer}
                        title={question.title}
                        minNumberOfAnswers={question.minNumberOfAnswers}
                        maxNumberOfAnswers={question.maxNumberOfAnswers}
                        options={question.propositions}
                    />
                );
            case QuestionType.GROUPED:
            case QuestionType.ASSOCIATION:
                return (
                    <GroupedQuestion
                        onValidate={sendQuizAnswer}
                        title={question.title}
                        subTitles={question.subTitles}
                        options={question.propositions}
                        isAssociationQuestion={
                            question.type === QuestionType.ASSOCIATION
                        }
                    />
                );
            case QuestionType.ORDERED_ANSWERS:
            case QuestionType.CORRECT_ORDERED_ANSWERS:
                return (
                    <OrderedQuestion
                        onValidate={sendQuizAnswer}
                        options={question.propositions}
                        title={question.title}
                    />
                );
            case QuestionType.GROUPED_EVALUATION:
                return (
                    <GroupedEvaluation
                        title={question.title}
                        evaluationQuestions={question.evaluationQuestions}
                        minValue={question.minValue}
                        maxValue={question.maxValue}
                        useDecimal={question.useDecimal}
                        evaluationDisplayType={question.evaluationDisplayType}
                        publicKey={vote.publicKey}
                    />
                );
            case QuestionType.DELIBERATION:
                return (
                    <Deliberation
                        question={question}
                        user={user}
                        proxies={user.proxies}
                        publicKey={vote.publicKey}
                    />
                );
            case QuestionType.ELECTION:
                return (
                    <Election
                        question={question}
                        user={user}
                        proxies={user.proxies}
                        publicKey={vote.publicKey}
                    />
                );
                break;
        }

        return renderNoVotePendingTemplate();
    };

    /**
     * Useful for zoom
     * OnClick inside Draggable doesn't work on mobile mode
     * https://github.com/react-grid-layout/react-draggable/issues/550#issuecomment-1338009022
     */
    const handleDragStart = (e: DraggableEvent, data: DraggableData) => {
        setDragInfo({
            x: data.x,
            y: data.y,
            time: Date.now(),
        });
    };

    /**
     * Useful for zoom
     * OnClick inside Draggable doesn't work on mobile mode
     * https://github.com/react-grid-layout/react-draggable/issues/550#issuecomment-1338009022
     *
     * The comparison of `e.type` to "touchend" is used to determine if the event
     * was triggered on a mobile device. On mobile, touch events like "touchend" are
     * used instead of mouse events like "mouseup".
     *
     * Notice that triggering a click event unconditionally (without any checks) does not work
     * reliably in non-mobile environments. This could be due to differences in how
     * browsers handle event propagation and timing between touch and mouse events.
     */
    const handleDragStop = (e: DraggableEvent, data: DraggableData) => {
        if (!dragInfo || "touchend" !== e.type) return;

        const change = {
            x: Math.abs(data.x - dragInfo.x),
            y: Math.abs(data.y - dragInfo.y),
            time: Date.now() - dragInfo.time,
        };

        const isNotMove = change.x + change.y <= 10;
        const isClickTiming = change.time < 300;
        const shouldApplyClick = isNotMove && isClickTiming;

        if (!shouldApplyClick) {
            return;
        }

        const clickEvent = new MouseEvent("click", {
            bubbles: true,
            cancelable: true,
        });

        (e.target as EventTarget).dispatchEvent(clickEvent);
    };

    if (error) {
        return <ErrorPage error={error} />;
    }

    if (!user) {
        return <Navigate replace to={`/login/${onlineCode}`} />;
    }

    if (user.onlineCode !== onlineCode) {
        //Setting timeout trick makes this operation safe
        //https://stackoverflow.com/a/69236626/22263049
        setTimeout(() => setUser(null), 0);
        return <Navigate replace to={`/login/${onlineCode}`} />;
    }

    const isZoomAvailable =
        session &&
        session.streamingLogin?.length &&
        session.streamingPassword?.length &&
        session.zoomSignature?.length;

    if (isZoomAvailable && isVideoAllowed) {
        const lang =
            LanguageMap[i18n.language as keyof typeof LanguageMap] || "en-US";
        loadZoomMeetingClient({
            lang,
            user,
            session,
            setSession,
        });

        return (
            <Draggable
                bounds={false}
                handle=".handle"
                onStart={handleDragStart}
                onStop={handleDragStop}
            >
                <ZoomVoteDesign>
                    <ParticipantNameWrapper className="handle">
                        <StyledName>
                            {user.displayName}
                            {session && liveDeviceCount > 1 && (
                                <StyledSpanDevicesCount>
                                    ({liveDeviceCount}
                                    <span>{t("connectedDevices")}</span>
                                    <DevicesIcon />)
                                </StyledSpanDevicesCount>
                            )}
                        </StyledName>
                    </ParticipantNameWrapper>
                    <div>{renderComponentVoteTemplate()}</div>
                    <StreamingToggleStyled
                        onClick={() => {
                            if (window.confirm(t("disableVideoConfirm"))) {
                                setIsAllowedVideoAllowed(!isVideoAllowed);
                            }
                        }}
                    >
                        <img
                            src={DisableStreamingLogo}
                            alt="Enabled streaming"
                        />
                        <span>{t("disableVideo")}</span>
                    </StreamingToggleStyled>
                </ZoomVoteDesign>
            </Draggable>
        );
    }

    return (
        <Layout isSocketError={isSocketError}>
            <>
                <StyledName>
                    {user.displayName}
                    {session && liveDeviceCount > 1 && (
                        <StyledSpanDevicesCount>
                            <DevicesIcon />
                            {liveDeviceCount}
                            <span>{t("connectedDevices")}</span>
                        </StyledSpanDevicesCount>
                    )}
                </StyledName>

                {renderComponentVoteTemplate()}

                {isZoomAvailable && !isVideoAllowed && (
                    <StreamingToggleStyled
                        onClick={() => {
                            setIsAllowedVideoAllowed(true);
                        }}
                    >
                        <img
                            src={EnableStreamingLogo}
                            alt="Enabled streaming "
                        />
                        <span>{t("enableVideo")}</span>
                    </StreamingToggleStyled>
                )}
            </>
        </Layout>
    );
};

const ZoomVoteDesign = styled.div`
    @media (min-width: 768px) {
        max-width: 320px;
    }
    position: absolute;
    width: 310px;
    background: #fff;
    border-radius: 4px;
    padding: 16px 8px;
    float: left;
    cursor: move;
    border: 1px solid #c1c1c1;
    box-shadow: 0 0 4px 2px #888585a6;
    top: 15px;
    right: 15px;
    margin: 0; /* à garder pour le draggable */
    max-height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
`;

const NoVoteStyled = styled.div`
    display: flex;
    flex-direction: column;
    line-height: 1.5rem;
    font-size: 14px;
    > p {
        margin-top: 0.5rem;
        margin-bottom: 0.5rem;
    }
`;

const StyledSpanDevicesCount = styled.span`
    align-items: center;
    display: flex;
    width: 3.5rem;
    justify-content: space-between;
    margin-left: 1rem;
    width: 166px;
    margin-left: auto;
    font-weight: 100;
`;

const StyledName = styled.h1`
    font-size: 14px;
    margin-bottom: 0.5rem;
    width: 100%;
    text-align: left;
    color: #626262;
    display: flex;
    align-items: center;
`;

const StyledNoVoteParagraph = styled.h1`
    font-size: 14px;
    margin-bottom: 0.5rem;
    width: 100%;
    text-align: left;
    color: #d9001b;
    font-weight: 100;
`;

const ConnectedMessage = styled.p`
    margin-top: 50px;
`;

//Mobile dragger hook for zoom
const ParticipantNameWrapper = styled.div`
    height: 3.5rem;
`;

const StreamingToggleStyled = styled.div`
    z-index: 5;
    cursor: pointer;
    max-width: 150px;
    margin: 24px auto 8px;
    text-align: center;
    font-size: 10px;
    color: #607d91;

    img {
        width: 75px;
        display: block;
        margin: auto;
    }
`;

export default SessionPage;
